import AltRouteIcon from "@mui/icons-material/AltRoute";
import ListIcon from "@mui/icons-material/List";
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { CityEscapesDropdown } from "../../components/CityEscapesDropdown.component";
import { ChallengeDetails, CityEscapeData } from "../../types/cityEscape";
import { EnigmasFlowChart } from "./EnigmasFlowChart";
import { EngimasList } from "./EnigmasList";

export type EnigmaSelectorProps = {
  cityEscapes?: CityEscapeData[];
  defaultMode?: "list" | "flow";
  isLoading?: boolean;
  selectedEscapeId?: string | undefined | null;
  selectedEnigmaId?: string | undefined | null;
  onCityEscapeChange?: (cityEscape: CityEscapeData | null) => void;
  onEnigmaChange?: (enigma: ChallengeDetails | null) => void;
};

export function EnigmaSelector({
  cityEscapes,
  isLoading,
  selectedEscapeId,
  selectedEnigmaId,
  defaultMode = "flow",
  onCityEscapeChange,
  onEnigmaChange,
}: EnigmaSelectorProps) {
  const [mode, setMode] = useState<"list" | "flow">(defaultMode);
  const [cityEscape, setCityEscape] = useState<
    CityEscapeData | undefined | null
  >(cityEscapes?.find((ce) => ce.id === selectedEscapeId));

  const handleCityEscapeChange = (ce: CityEscapeData | null) => {
    console.debug("City escape changed:", ce);
    setCityEscape(ce);
    onCityEscapeChange?.(ce);
    onEnigmaChange?.(null);
  };

  useEffect(() => {
    if (selectedEscapeId && cityEscapes) {
      const selected = cityEscapes.find((ce) => ce.id === selectedEscapeId);
      setCityEscape(selected ?? null);
    }
  }, [selectedEscapeId, cityEscapes]);

  const enigmas = useMemo(
    () => cityEscape?.scenes.flatMap((scene) => scene.challenges) || [],
    [cityEscape, selectedEscapeId]
  );

  return (
    <Box id="enigmas-drawer-content" role="presentation">
      <Stack direction="row" spacing={2} useFlexGap={true} padding={1}>
        <CityEscapesDropdown
          cityEscapes={cityEscapes || []}
          selectedId={selectedEscapeId || undefined}
          onChange={handleCityEscapeChange}
          sx={{ flex: 1 }}
        />
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(event, newMode) => setMode(newMode)}
          aria-label="text alignment"
        >
          <Tooltip title="Visualizzazione elenco">
            <ToggleButton value="list" aria-label="left aligned">
              <ListIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Visualizzazione a grafo">
            <ToggleButton value="flow" aria-label="centered">
              <AltRouteIcon />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Stack>
      {(mode == "list" && (
        <Box padding={1}>
          <EngimasList
            enigmas={enigmas}
            isLoading={isLoading}
            selectedId={selectedEnigmaId || undefined}
            onSelection={(enigma) => {
              onEnigmaChange?.(enigma);
            }}
          />
        </Box>
      )) ||
        (mode == "flow" && (
          <Box padding={1}>
            <EnigmasFlowChart
              enigmas={enigmas}
              isLoading={isLoading}
              selectedId={selectedEnigmaId || undefined}
              onSelection={(enigma) => {
                onEnigmaChange?.(enigma);
              }}
            />
          </Box>
        ))}
    </Box>
  ); // Component implementation goes here
}
